import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  const [appointmentData, setAppointmentData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    preferred_date: '',
    preferred_time: '',
    message: '',
  });

  const [activeTab, setActiveTab] = useState('contact');

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAppointmentChange = (e) => {
    const { name, value } = e.target;
    setAppointmentData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setAppointmentData((prev) => ({ ...prev, service: value }));
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.phone || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! Your inquiry has been submitted. We will get back to you soon.');
      const msg = `NEW CONTACT INQUIRY\nName: ${formData.name}\nPhone: ${formData.phone}\nEmail: ${formData.email}\nSubject: ${formData.subject}\nMessage: ${formData.message}`;
      window.open(`https://wa.me/917010200869?text=${encodeURIComponent(msg)}`, '_blank');
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to submit inquiry. Please try again.');
    }
  };

  const handleAppointmentSubmit = async (e) => {
    e.preventDefault();

    if (
      !appointmentData.name ||
      !appointmentData.phone ||
      !appointmentData.email ||
      !appointmentData.service ||
      !appointmentData.preferred_date ||
      !appointmentData.preferred_time
    ) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API}/appointments`, appointmentData);
      toast.success('Appointment request submitted successfully! We will confirm your appointment shortly.');
      // Send WhatsApp notification to AMC
      const msg = `NEW APPOINTMENT\nName: ${appointmentData.name}\nPhone: ${appointmentData.phone}\nEmail: ${appointmentData.email}\nService: ${appointmentData.service}\nDate: ${appointmentData.preferred_date}\nTime: ${appointmentData.preferred_time}\nNotes: ${appointmentData.message || 'None'}`;
      window.open(`https://wa.me/917010200869?text=${encodeURIComponent(msg)}`, '_blank');
      setAppointmentData({
        name: '',
        phone: '',
        email: '',
        service: '',
        preferred_date: '',
        preferred_time: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting appointment:', error);
      toast.error('Failed to book appointment. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <section className="bg-gradient-to-r from-[#0A2540] to-[#0EA5E9] py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="contact-heading"
          >
            Contact Us
          </h1>
          <p className="text-xl text-white/90">We're here to help you 24/7</p>
        </div>
      </section>

      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
            <div>
              <h2
                className="text-3xl font-bold text-[#0A2540] mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Get in Touch
              </h2>
              <p className="text-[#475569] mb-8">
                Have questions or need assistance? We're always ready to help. Reach out to us through any of the following channels.
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="bg-[#0EA5E9]/10 rounded-full p-3 mr-4">
                    <Phone className="h-6 w-6 text-[#0EA5E9]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A2540] mb-1">Phone</h3>
                    <p className="text-[#475569]">Emergency: <a href="tel:7010200869" className="text-[#0EA5E9] hover:underline">7010200869</a></p>
                    <p className="text-[#475569]">WhatsApp: <a href="https://wa.me/917010200869" className="text-[#0EA5E9] hover:underline">7010200869</a></p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-[#0EA5E9]/10 rounded-full p-3 mr-4">
                    <MapPin className="h-6 w-6 text-[#0EA5E9]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A2540] mb-1">Location</h3>
                    <p className="text-[#475569]">No: 29, Gandhi Main Road, Puzhal, Chennai 66</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="bg-[#0EA5E9]/10 rounded-full p-3 mr-4">
                    <Clock className="h-6 w-6 text-[#0EA5E9]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A2540] mb-1">Hours</h3>
                    <p className="text-[#475569]">24/7 - Always Open</p>
                    <p className="text-[#475569] text-sm">Emergency services available round the clock</p>
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="font-semibold text-[#0A2540] mb-4">Find Us on Map</h3>
                <div className="w-full h-80 rounded-2xl overflow-hidden shadow-lg">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3885.5!2d80.1488!3d13.1247!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a5263d3b1d8e3ed%3A0x4b715e0a8e8b8c8!2sAaric%20Medical%20Center!5e0!3m2!1sen!2sin!4v1700000000000"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="AMC Location"
                    data-testid="google-map-iframe"
                  ></iframe>
                </div>
                <a
                  href="https://share.google/8H1BX0oKsyh4l0ckz"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-3 text-[#0EA5E9] hover:underline"
                  data-testid="google-maps-link"
                >
                  View on Google Maps
                </a>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="flex space-x-4 mb-6 border-b">
                <button
                  onClick={() => setActiveTab('contact')}
                  className={`pb-3 px-4 font-semibold transition-all ${
                    activeTab === 'contact'
                      ? 'text-[#0EA5E9] border-b-2 border-[#0EA5E9]'
                      : 'text-[#475569] hover:text-[#0A2540]'
                  }`}
                  data-testid="contact-tab"
                >
                  General Inquiry
                </button>
                <button
                  onClick={() => setActiveTab('appointment')}
                  className={`pb-3 px-4 font-semibold transition-all ${
                    activeTab === 'appointment'
                      ? 'text-[#0EA5E9] border-b-2 border-[#0EA5E9]'
                      : 'text-[#475569] hover:text-[#0A2540]'
                  }`}
                  data-testid="appointment-tab"
                >
                  Book Appointment
                </button>
              </div>

              {activeTab === 'contact' ? (
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleContactChange}
                      required
                      className="mt-1"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleContactChange}
                        required
                        className="mt-1"
                        data-testid="contact-email-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleContactChange}
                        required
                        className="mt-1"
                        data-testid="contact-phone-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="subject">Subject *</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleContactChange}
                      required
                      className="mt-1"
                      data-testid="contact-subject-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleContactChange}
                      required
                      rows={5}
                      className="mt-1"
                      data-testid="contact-message-textarea"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#0A2540] text-white hover:bg-[#0A2540]/90 rounded-full py-6 text-lg font-semibold"
                    data-testid="submit-contact-form-button"
                  >
                    <Send className="mr-2 h-5 w-5" />
                    Send Message
                  </Button>
                </form>
              ) : (
                <form onSubmit={handleAppointmentSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="appt_name">Full Name *</Label>
                    <Input
                      id="appt_name"
                      name="name"
                      value={appointmentData.name}
                      onChange={handleAppointmentChange}
                      required
                      className="mt-1"
                      data-testid="appointment-name-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="appt_phone">Phone *</Label>
                      <Input
                        id="appt_phone"
                        name="phone"
                        value={appointmentData.phone}
                        onChange={handleAppointmentChange}
                        required
                        className="mt-1"
                        data-testid="appointment-phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="appt_email">Email *</Label>
                      <Input
                        id="appt_email"
                        name="email"
                        type="email"
                        value={appointmentData.email}
                        onChange={handleAppointmentChange}
                        required
                        className="mt-1"
                        data-testid="appointment-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="service">Service *</Label>
                    <Select onValueChange={handleServiceChange} value={appointmentData.service}>
                      <SelectTrigger className="mt-1" data-testid="appointment-service-select">
                        <SelectValue placeholder="Select a service" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="general-consultation">General Consultation</SelectItem>
                        <SelectItem value="infertility">Infertility Treatment</SelectItem>
                        <SelectItem value="pregnancy-care">Pregnancy Care</SelectItem>
                        <SelectItem value="orthopedics">Orthopedics</SelectItem>
                        <SelectItem value="dermatology">Dermatology</SelectItem>
                        <SelectItem value="pediatrics">Pediatrics</SelectItem>
                        <SelectItem value="diagnostic-test">Diagnostic Test</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="preferred_date">Preferred Date *</Label>
                      <Input
                        id="preferred_date"
                        name="preferred_date"
                        type="date"
                        value={appointmentData.preferred_date}
                        onChange={handleAppointmentChange}
                        required
                        className="mt-1"
                        data-testid="appointment-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="preferred_time">Preferred Time *</Label>
                      <Input
                        id="preferred_time"
                        name="preferred_time"
                        type="time"
                        value={appointmentData.preferred_time}
                        onChange={handleAppointmentChange}
                        required
                        className="mt-1"
                        data-testid="appointment-time-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="appt_message">Additional Notes</Label>
                    <Textarea
                      id="appt_message"
                      name="message"
                      value={appointmentData.message}
                      onChange={handleAppointmentChange}
                      rows={3}
                      className="mt-1"
                      placeholder="Any specific concerns or requirements..."
                      data-testid="appointment-message-textarea"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-[#0A2540] text-white hover:bg-[#0A2540]/90 rounded-full py-6 text-lg font-semibold"
                    data-testid="submit-appointment-button"
                  >
                    Book Appointment
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;